import React, { useCallback, useState, useEffect, useMemo } from 'react'
import { Alert, Dialog, Loader, Toast, useConfirm } from '@refera/ui-web'
import { Danger as DangerIcon, TickCircle as SuccessIcon, DocumentCopy } from '@refera/ui-icons'
import Theme from '@refera/ui-core'
import useStyles from './styles'
import { navigate, useLocation } from '@reach/router'
import {
  Grid,
  IconButton,
  InputLabel,
  TextField,
  Collapse,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core'
import { useFormContext, Controller } from 'react-hook-form'
import { useSelector, useDispatch } from 'react-redux'
import InputMask from 'react-input-mask'
import {
  approveBudgetLoadingSelector,
  getBudgetByIdSelector,
  getPayerOptionsSelector,
  getStepStatusLogSelector,
  updateBudgetErrorSelector,
  updateBudgetLoadingSelector,
} from '_modules/budget/selectors'
import {
  updateBudget,
  getPayerOptions,
  approveBudget,
  getBudget,
  generateBudgetPaymentLink,
  getStepStatusLog,
  saveManualBudgetPaymentLink,
  verifyPaidInstallment,
} from '_modules/budget/actions'
import { getInstallmentRules } from '_/modules/finance/actions'
import { getInstallmentRulesSelector } from '_/modules/finance/selectors'
import TextFieldComponent from '_components/textfield'
import { userSelector } from '_modules/authentication/selectors'
import useCanDoButtonAction from '_hooks/use-can-do-button-action'
import { EMAIL_REGEX, ROUTES } from '_utils/constants'
import { BUTTONS_ACTION, ROLE_GROUP_CHOICES, STEP_STATUS } from '_utils/constants/service-order'
import {
  currentServiceOrderSelector,
  getButtonsActionLoadingSelector,
  getButtonsActionSelector,
  getButtonsActionByNameSelector,
} from '_modules/service-orders/selectors'
import { cpfCnpjMask, ERRORS, removeCountryId, transformData, validateFormCpfCnpj } from './utils'
import InputField from './input-field'
import { getButtonsAction, getServiceOrder } from '_/modules/service-orders/actions'
import { getHistoryLogs } from '_/modules/history-logs/actions'
import { agencySelector } from '_/modules/agency/selectors'
import { getAgency } from '_/modules/agency/actions'
import { AlertTitle } from '@material-ui/lab'
import SelectField from '../components/SelectField'
import { formatErrorMessage } from '../utils/FormatErrorMessage'
import ConfirmationDialog from '_/components/help-requests/modals/confirmation-dialog'
import useRolePermission from '_/hooks/use-role-permission'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'
import { getMessagingByServiceOrderId } from '_/modules/mensageria/actions'
import { openWhatsAppWithMessage } from '_/utils/open-whatsApp'
import { useToast } from '_/hooks/use-toast'
import { usePayerDataForm } from './use-payer-data-form'
import Button from '_components/button'
import {
  PAYMENT_LINK_ME_LIVREI_CONTRACT,
  PAYMENT_LINK_REFERA_PROFILE,
} from '_utils/constants/payment-link'

const getStepStatusName = stepStatus => {
  if (!stepStatus) return ''
  if (typeof stepStatus === 'string') return stepStatus
  if (typeof stepStatus?.get === 'function') return stepStatus.get('name') || ''
  return stepStatus?.name || ''
}

/**
 * @param {String} payload.budgetId
 * @param {String} payload.serviceOrderId
 * @param {Function} payload.onSubmitSuccess
 * @param {Boolean} payload.isApproving -Indicates if the form is being submitted for approval, mainly from kanban
 */
export const PayerDataForm = ({
  budgetId,
  serviceOrderId,
  onSubmitSuccess,
  isApproving = false,
}) => {
  const styles = useStyles()
  const loadingBudget = useSelector(updateBudgetLoadingSelector)
  const errorBudget = useSelector(updateBudgetErrorSelector)
  const loadingApprove = useSelector(approveBudgetLoadingSelector)
  const serviceOrder = useSelector(currentServiceOrderSelector)
  const user = useSelector(userSelector)
  const dispatch = useDispatch()
  const { showToast } = useToast()
  const { checkIfUserDoesNotPermission, isSAAS, isAdmin } = useRolePermission()

  const { pathname } = useLocation()

  const editPayerDataButtonName = useMemo(() => BUTTONS_ACTION.EDIT_PAYER_DATA, [])
  const paymentLinkButtonName = useMemo(() => BUTTONS_ACTION.GENERATE_PAYMENT_LINK, [])
  const initialModal = useMemo(() => {
    return { isOpen: false, message: '', icon: '', type: 'info', action: () => {} }
  }, [])
  const budget = useSelector(getBudgetByIdSelector(budgetId))
  const options = useSelector(getPayerOptionsSelector)
  const buttonsAction = useSelector(getButtonsActionSelector)
  const isButtonsActionLoading = useSelector(getButtonsActionLoadingSelector)
  const paymentLinkButtonAction = useSelector(getButtonsActionByNameSelector(paymentLinkButtonName))
  const stepStatusLog = useSelector(getStepStatusLogSelector)
  const agency = useSelector(agencySelector)
  const installmentRules = useSelector(getInstallmentRulesSelector)
  const agencyId = useMemo(() => serviceOrder?.agency?.get('id'), [serviceOrder?.agency])
  const [personPayingEmailError, setPersonPayingEmailError] = useState(null)
  const [emailInvoiceError, setEmailInvoiceError] = useState('')
  const [payer, setPayer] = useState({})
  const [modal, setModal] = useState(initialModal)
  const [error, setError] = useState({ isOpen: false, message: '' })
  const [toast, setToast] = useState({ isOpen: false, message: '', type: '' })
  const [hasPaidInstallment, setHasPaidInstallment] = useState(true)
  const [alertMessage, setAlertMessage] = useState('')
  const [isManualPaymentLink, setIsManualPaymentLink] = useState(false)
  const [manualPaymentLinkUrl, setManualPaymentLinkUrl] = useState('')
  const { isConfirmed } = useConfirm()
  const { setIsLoading, isLoading, setIsSubmitDisabled } = usePayerDataForm()

  const serviceOrderJS = useMemo(() => {
    return serviceOrder?.toJS()
  }, [serviceOrder])

  const isAgencyLoading = useMemo(
    () => Boolean(agencyId && (!agency || agency?.id !== agencyId)),
    [agencyId, agency]
  )

  const currentBudgetIsApproved = useMemo(() => budget?.get('approved'), [budget])
  const isBudgetRefused = useMemo(() => Boolean(budget?.get('refused')), [budget])
  const canEditPayerData = useCanDoButtonAction({ nameButton: editPayerDataButtonName })
  const isFromSAASAgencyAndNotReferaService = useMemo(() => {
    return (
      serviceOrderJS?.isReferaService === false && serviceOrderJS?.agency?.contractType === 'SAAS'
    )
  }, [serviceOrderJS])
  const isApproval = useMemo(
    () => pathname.slice('/').includes('aprovar') || isApproving,
    [pathname, isApproving]
  )
  const { watch, errors, register, control, handleSubmit, setValue, getValues } = useFormContext()
  const data = watch()
  const currentPaymentLink = useMemo(() => budget?.get('paymentLink'), [budget])
  const currentPaymentLinks = useMemo(() => budget?.get('paymentLinks'), [budget])
  const paymentLinkData = useMemo(() => {
    if (!currentPaymentLink) return null
    return typeof currentPaymentLink?.toJS === 'function'
      ? currentPaymentLink.toJS()
      : currentPaymentLink
  }, [currentPaymentLink])
  const paymentLinksData = useMemo(() => {
    if (currentPaymentLinks) {
      const parsedPaymentLinks =
        typeof currentPaymentLinks?.toJS === 'function'
          ? currentPaymentLinks.toJS()
          : currentPaymentLinks
      if (Array.isArray(parsedPaymentLinks) && parsedPaymentLinks.length > 0)
        return parsedPaymentLinks
    }

    // Mantem compatibilidade com respostas antigas que ainda retornam apenas `paymentLink`.
    return paymentLinkData?.url
      ? [{ ...paymentLinkData, id: paymentLinkData.id ?? `legacy-${paymentLinkData.url}` }]
      : []
  }, [currentPaymentLinks, paymentLinkData])
  const watchCPF = watch('responsibleDocNumber', payer?.responsibleDocNumber || '')

  const watchInvoiceDocNumber = watch('invoiceDocNumber', payer?.invoiceDocNumber || '')
  const watchInvoiceName = watch('nameInvoice', payer?.nameInvoice || '')
  const watchInvoiceEmail = watch('emailInvoice', payer?.emailInvoice || '')
  const watchTaxRegime = watch('taxRegime', payer?.taxRegime || '')

  const currentServiceOrderStepStatusName = useMemo(() => {
    return getStepStatusName(serviceOrder?.get?.('stepStatus') ?? serviceOrderJS?.stepStatus)
  }, [serviceOrder, serviceOrderJS?.stepStatus])
  const isWaitingPaymentInfo = useMemo(
    () => currentServiceOrderStepStatusName === STEP_STATUS.WAITING_PAYMENT_INFO,
    [currentServiceOrderStepStatusName]
  )
  const payerDataPermissionNames = useMemo(
    () => [PERMISSIONS.EDIT_BUDGET_PAYER_DATA, PERMISSIONS.EDIT_PAYER_DATA],
    []
  )
  const payerDataEditActions = useMemo(
    () => [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
    []
  )
  const hasPaymentLinkViewPermission = useMemo(() => {
    if (user?.isAdmin) return true

    return payerDataPermissionNames.some(
      permissionName =>
        user?.permissions?.[permissionName]?.includes(PERMISSIONS_ACTIONS.READ) ||
        payerDataEditActions.some(action => user?.permissions?.[permissionName]?.includes(action))
    )
  }, [payerDataEditActions, payerDataPermissionNames, user])
  const hasPaymentLinkEditPermission = useMemo(() => {
    if (user?.isAdmin) return true

    return payerDataPermissionNames.some(permissionName =>
      payerDataEditActions.some(action => user?.permissions?.[permissionName]?.includes(action))
    )
  }, [payerDataEditActions, payerDataPermissionNames, user])
  const canManagePaymentLink = useMemo(() => {
    const activeProfileName = user?.activeProfile?.name
    return (
      (activeProfileName === PAYMENT_LINK_REFERA_PROFILE || user?.isAdmin) &&
      serviceOrderJS?.agency?.contractType === PAYMENT_LINK_ME_LIVREI_CONTRACT
    )
  }, [serviceOrderJS?.agency?.contractType, user?.activeProfile?.name, user?.isAdmin])
  const isPaymentLinkStepAllowed = useMemo(() => {
    const allowedSteps = paymentLinkButtonAction?.optionsStepServiceOrder || []
    if (!currentServiceOrderStepStatusName) return false
    return allowedSteps.some(stepStatus => stepStatus?.name === currentServiceOrderStepStatusName)
  }, [paymentLinkButtonAction?.optionsStepServiceOrder, currentServiceOrderStepStatusName])
  const canManagePaymentLinkActions = useMemo(() => {
    if (!canManagePaymentLink) return false
    if (!hasPaymentLinkEditPermission) return false
    if (!paymentLinkButtonAction) return false
    return isPaymentLinkStepAllowed
  }, [
    paymentLinkButtonAction,
    canManagePaymentLink,
    hasPaymentLinkEditPermission,
    isPaymentLinkStepAllowed,
  ])
  const canInteractWithPaymentLinkActions = useMemo(
    () => canManagePaymentLinkActions && !isBudgetRefused,
    [canManagePaymentLinkActions, isBudgetRefused]
  )
  const shouldRenderPaymentLinkSection = useMemo(
    () =>
      canManagePaymentLink &&
      hasPaymentLinkViewPermission &&
      (hasPaymentLinkEditPermission ||
        canManagePaymentLinkActions ||
        paymentLinksData.length > 0 ||
        Boolean(paymentLinkData?.url) ||
        paymentLinkData?.status === 'failed'),
    [
      canManagePaymentLink,
      canManagePaymentLinkActions,
      hasPaymentLinkEditPermission,
      hasPaymentLinkViewPermission,
      paymentLinkData?.status,
      paymentLinkData?.url,
      paymentLinksData.length,
    ]
  )
  const isPaymentLinkUnsupportedMethod = useMemo(
    () =>
      ['realstate_installments', 'realestate_installment', 'rent_discount'].includes(
        data?.formOfPayment
      ),
    [data?.formOfPayment]
  )
  const shouldShowBoletoPixInstallmentAlert = useMemo(
    () =>
      ['pix', 'boleto'].includes(data?.formOfPayment) && Number(data?.installmentQuantity || 0) > 1,
    [data?.formOfPayment, data?.installmentQuantity]
  )
  const paymentLinkBlockedReason = useMemo(() => {
    if (!canManagePaymentLink) {
      return 'A geração do link ASAAS está disponível apenas para usuários Refera ou admins em agências do contrato Me Livrei.'
    }
    if (!hasPaymentLinkViewPermission) {
      return 'Usuário sem permissão para visualizar os dados do pagador.'
    }
    if (!hasPaymentLinkEditPermission) {
      return 'Usuário sem permissão para editar os dados do pagador e gerenciar o link de pagamento.'
    }
    if (isBudgetRefused) {
      return 'Não é permitido gerar link de pagamento para orçamentos recusados.'
    }
    if (hasPaidInstallment) {
      return 'As informações não podem ser alteradas, pois o pagamento já foi realizado.'
    }
    if (!canManagePaymentLinkActions) {
      return 'O chamado não está em uma etapa que permita gerar ou atualizar o link de pagamento.'
    }
    if (data?.payCollector !== 'refera_collector') {
      return 'Selecione a Refera como responsável pela cobrança para habilitar o link ASAAS.'
    }
    if (!data?.formOfPayment) {
      return 'Selecione a forma de pagamento antes de gerar o link.'
    }
    if (!Number(data?.installmentQuantity || 0)) {
      return 'Selecione o parcelamento desejado antes de gerar o link.'
    }
    if (shouldShowBoletoPixInstallmentAlert) {
      return 'Para pagamentos com Boleto ou Pix não é permitido parcelamento.'
    }
    return ''
  }, [
    canManagePaymentLinkActions,
    canInteractWithPaymentLinkActions,
    canManagePaymentLink,
    data?.formOfPayment,
    data?.installmentQuantity,
    data?.payCollector,
    hasPaidInstallment,
    hasPaymentLinkEditPermission,
    hasPaymentLinkViewPermission,
    isBudgetRefused,
    shouldShowBoletoPixInstallmentAlert,
  ])
  const automaticPaymentLinkBlockedReason = useMemo(() => {
    if (isPaymentLinkUnsupportedMethod) {
      return 'Não é permitido gerar link de pagamento para desconto imobiliária ou desconto no aluguel.'
    }
    return paymentLinkBlockedReason
  }, [isPaymentLinkUnsupportedMethod, paymentLinkBlockedReason])

  const initialState = useCallback(
    initialData => {
      register('regime')
      register('taxRegime')
      register('nameInvoice')
      register('emailInvoice')
      register('payCollector')
      register('formOfPayment')
      register('responsibleName')
      register('recipientInvoice')
      register('invoiceDocNumber')
      register('personPayingEmail')
      register('paymentResponsible')
      register('whatsappResponsible')
      register('installmentQuantity')
      register('responsibleDocNumber')

      const payCollectorInitialValue = isFromSAASAgencyAndNotReferaService
        ? 'intermediary_collector'
        : initialData?.payCollector
      setValue('payCollector', payCollectorInitialValue || '')
      setValue('formOfPayment', initialData?.formOfPayment || '')
      setValue('responsibleName', initialData?.responsibleName || '')
      setValue('recipientInvoice', initialData?.recipientInvoice || '')
      setValue('personPayingEmail', initialData?.personPayingEmail || '')
      setValue('paymentResponsible', initialData?.paymentResponsible || '')
      setValue('installmentQuantity', initialData?.installmentQuantity || '')
      setValue('whatsappResponsible', initialData?.whatsappResponsible || '')
      setValue('responsibleDocNumber', initialData?.responsibleDocNumber || '')
      setValue('regime', initialData?.regime || '')
      setValue('taxRegime', initialData?.taxRegime || '')
      setValue('nameInvoice', initialData?.nameInvoice || '')
      setValue('emailInvoice', initialData?.emailInvoice || '')
      setValue('invoiceDocNumber', initialData?.invoiceDocNumber || '')
    },
    [budget, payer, data, isFromSAASAgencyAndNotReferaService]
  )

  const handleGetBudget = useCallback(() => {
    dispatch(getBudget(serviceOrderId, budgetId))
  }, [dispatch, serviceOrderId, budgetId])

  const handleGetAgency = useCallback(() => {
    dispatch(getAgency(agencyId))
  }, [dispatch, agencyId])

  const handleGetButtonsAction = useCallback(() => {
    if (buttonsAction === undefined && !isButtonsActionLoading) dispatch(getButtonsAction())
    if (!stepStatusLog?.length) dispatch(getStepStatusLog(serviceOrderId, budgetId))
  }, [
    budgetId,
    buttonsAction,
    dispatch,
    isButtonsActionLoading,
    serviceOrderId,
    stepStatusLog?.length,
  ])

  const handleVerifyPaidInstallment = async () => {
    await Promise.resolve(dispatch(verifyPaidInstallment(serviceOrderId, budgetId)))
      .then(() => setHasPaidInstallment(false))
      .catch(() => {
        setError({
          isOpen: true,
          message: ERRORS.CANNOT_EDIT_BUDGET_PAID,
        })
        setHasPaidInstallment(true)
      })
  }

  useEffect(() => {
    if (!serviceOrder) dispatch(getServiceOrder(serviceOrderId))
    if (!budget) handleGetBudget()
  }, [budget, serviceOrder])

  useEffect(() => {
    if (agencyId && (!agency || agency?.id !== agencyId)) {
      handleGetAgency()
    }
  }, [agencyId, agency, handleGetAgency])

  useEffect(() => {
    if (buttonsAction === undefined || !stepStatusLog?.length) handleGetButtonsAction()
    if (!installmentRules && budgetId) dispatch(getInstallmentRules({ budgetId }))
  }, [
    buttonsAction,
    stepStatusLog?.length,
    installmentRules,
    budgetId,
    handleGetButtonsAction,
    dispatch,
  ])

  useEffect(() => {
    dispatch(getPayerOptions(serviceOrderId, budgetId))
    handleVerifyPaidInstallment()
  }, [budgetId, dispatch, serviceOrderId])

  useEffect(() => {
    if (budget?.budgetPayer) {
      const initialData = budget?.toJS().budgetPayer[0]
      setPayer(initialData)
      initialState(initialData)
    }
  }, [budget?.budgetPayer])

  useEffect(() => {
    if (paymentLinkData?.mode === 'manual') {
      setIsManualPaymentLink(true)
      setManualPaymentLinkUrl(paymentLinkData?.url || '')
      return
    }

    setIsManualPaymentLink(false)
    setManualPaymentLinkUrl('')
  }, [paymentLinkData?.mode, paymentLinkData?.url])

  useEffect(() => {
    if (isWaitingPaymentInfo) {
      setToast({
        isOpen: true,
        message: 'Dados requeridos porque o chamado está na etapa "Aguardando dados de pagamento"',
        type: 'info',
      })
    }
  }, [isWaitingPaymentInfo])

  const handleBackNavigation = useCallback(() => {
    dispatch(getHistoryLogs(serviceOrderId))
    return navigate(`${ROUTES.SERVICE_ORDER}/${serviceOrderId}/orcamento/${budgetId}`)
  }, [navigate, serviceOrderId, budgetId, dispatch])

  const PAYER_OPTIONS = options?.paymentResponsible
  const REGIMEN_OPTIONS = options?.regime
  const CHARGING_OPTIONS = options?.payCollector
  const PAYMENT_OPTIONS = options?.paymentMethod
  const INSTALLMENT_OPTIONS = options?.installmentOptions
  const INSTALLMENT_OPTIONS_WITHOUT_INTEREST = options?.installmentOptionsWithoutInterest

  const installmentSelectOptions = useMemo(() => {
    return data?.formOfPayment === 'realestate_installment'
      ? INSTALLMENT_OPTIONS_WITHOUT_INTEREST
      : INSTALLMENT_OPTIONS
  }, [data?.formOfPayment, INSTALLMENT_OPTIONS_WITHOUT_INTEREST, INSTALLMENT_OPTIONS])

  const RECIPIENT_OPTIONS = [
    { option: 'Responsável pelo pagamento', status: 'responsible_above' },
    { option: 'Outro', status: 'other' },
  ]

  const hasPermissionToEditPayerData = useMemo(() => {
    const permissionActions = [PERMISSIONS_ACTIONS.EDIT]
    const permission = PERMISSIONS.EDIT_BUDGET_PAYER_DATA
    if (permissionActions.some(action => user?.permissions?.[permission]?.includes(action))) {
      if (!canEditPayerData) {
        return true
      }
    }
    return false
  }, [user, canEditPayerData])

  const isSubmitDisabled = useMemo(() => {
    const disabled = (!canEditPayerData || hasPaidInstallment) && !hasPermissionToEditPayerData
    setIsSubmitDisabled(disabled)
    return disabled
  }, [hasPaidInstallment, canEditPayerData, hasPermissionToEditPayerData, setIsSubmitDisabled])

  const handleAutoCompleteResponsible = useCallback(() => {
    if (data?.paymentResponsible === 'requester' && payer?.paymentResponsible !== 'requester') {
      setValue('responsibleName', serviceOrder?.contactName || '')
      setValue('personPayingEmail', serviceOrder?.contactEmail || '')
      setValue(
        'whatsappResponsible',
        serviceOrder?.contactNumber ? removeCountryId(serviceOrder?.contactNumber) : ''
      )
      setValue(
        'responsibleDocNumber',
        serviceOrder?.contactCpfCnpj ? cpfCnpjMask(serviceOrder?.contactCpfCnpj) : ''
      )
      return
    }
    if (data.paymentResponsible === 'owner' && payer?.paymentResponsible !== 'owner') {
      setValue('responsibleName', serviceOrder?.propertyName || '')
      setValue('personPayingEmail', serviceOrder?.propertyEmail || '')
      setValue(
        'whatsappResponsible',
        serviceOrder?.propertyWhatsapp ? removeCountryId(serviceOrder?.propertyWhatsapp) : ''
      )
      setValue(
        'responsibleDocNumber',
        serviceOrder?.propertyCpfOrCnpj ? cpfCnpjMask(serviceOrder?.propertyCpfOrCnpj) : ''
      )
      if (payer?.personPayingEmail) {
        if (!isValidEmail(payer?.personPayingEmail)) {
          setPersonPayingEmailError('Invalid Person Paying Email')
        } else {
          setPersonPayingEmailError(null)
        }
      }
      return
    }
    if (
      data.paymentResponsible === 'intermediary' &&
      payer?.paymentResponsible !== 'intermediary'
    ) {
      setValue('responsibleName', agency?.personResponsibleName || '')
      setValue('personPayingEmail', agency?.contactEmail || '')
      setValue(
        'whatsappResponsible',
        agency?.phoneNumber ? removeCountryId(agency?.phoneNumber) : ''
      )
      setValue('responsibleDocNumber', agency?.cpfCnpj ? cpfCnpjMask(agency?.cpfCnpj) : '')
      return
    }
    setValue('responsibleName', payer?.responsibleName || '')
    setValue('personPayingEmail', payer?.personPayingEmail || '')
    setValue(
      'whatsappResponsible',
      payer?.whatsappResponsible ? removeCountryId(payer?.whatsappResponsible) : ''
    )
    setValue(
      'responsibleDocNumber',
      payer?.responsibleDocNumber ? cpfCnpjMask(payer?.responsibleDocNumber) : ''
    )
  }, [data?.paymentResponsible, serviceOrder, payer, agency])

  useEffect(() => {
    handleAutoCompleteResponsible()
  }, [data?.paymentResponsible])

  const isValidEmail = email => EMAIL_REGEX.test(email)
  const isValidPaymentLinkUrl = useCallback(url => {
    if (!url?.trim()) return false

    try {
      const parsedUrl = new URL(url.trim())
      return parsedUrl.protocol === 'https:'
    } catch {
      return false
    }
  }, [])

  const handleChange = event => {
    if (event.target.name === 'personPayingEmail') {
      if (!isValidEmail(event.target.value)) {
        setPersonPayingEmailError('Invalid Person Paying Email')
      } else {
        setPersonPayingEmailError(null)
      }
    }
    if (event.target.name === 'emailInvoice') {
      if (!isValidEmail(event.target.value)) {
        setEmailInvoiceError('Invalid Invoice Email')
      } else {
        setEmailInvoiceError(null)
        setValue('emailInvoice', event.target.value)
      }
    }
  }

  useEffect(() => {
    if (payer?.emailInvoice) {
      setEmailInvoiceError(null)
    }
    if (payer?.personPayingEmail) {
      setPersonPayingEmailError(null)
    }
  }, [payer?.emailInvoice, payer?.personPayingEmail])

  const handleCloseModal = useCallback(() => {
    setModal(old => ({ ...old, isOpen: false }))
  }, [setModal])

  const handleInstallmentQuantity = useCallback(
    async installmentQuantity => {
      if (isAdmin) {
        if (installmentRules) {
          const higherInstallment = installmentRules?.installment
          if (higherInstallment && installmentQuantity > higherInstallment) {
            setAlertMessage(
              `A quantidade do parcelamento é maior que a quantidade máxima permitida em função do valor do orçamento (${higherInstallment} parcela${
                higherInstallment > 1 ? 's' : ''
              }).\nVocê confirma esta informação?`
            )
            const confirmed = await isConfirmed()
            return confirmed
          }
          return true
        }
      } else {
        if (serviceOrderJS?.agency?.contractType === 'SAAS') {
          return true
        }
        if (installmentQuantity === 9 || installmentQuantity === 10) {
          setToast({
            isOpen: true,
            type: 'error',
            message:
              'Pedimos desculpas, mas o parcelamento em 9 ou 10 vezes só pode ser escolhido pelos colaboradores da Refera.',
          })
          return false
        }
        if (installmentQuantity < 9) {
          if (installmentRules) {
            const higherInstallment = installmentRules?.installment
            if (
              installmentRules.minValue <= budget.priceTotal &&
              budget.priceTotal <= installmentRules.maxValue
            ) {
              if (installmentQuantity > higherInstallment) {
                setToast({
                  isOpen: true,
                  type: 'error',
                  message: `Por regras da Refera, e com base no valor do orçamento, seu pagamento só pode ser parcelado em até ${higherInstallment} vezes.`,
                })
                return false
              }
            }
            return true
          }
        }
        setToast({
          isOpen: true,
          type: 'error',
          message:
            'Pedimos desculpas, mas o parcelamento acima de 10 vezes só pode ser escolhido pelos colaboradores da Refera.',
        })
        return false
      }
      return true
    },
    [installmentRules, budget, serviceOrderJS]
  )

  const onFinalizesUpdateBudget = useCallback(
    async (updateError = false) => {
      setIsLoading(true)
      if (updateError) {
        if (errorBudget?.get('errorCode')) {
          let errorMessage = ''

          if (errorBudget?.get('errorCode') === 'forbidden_user_data') {
            errorMessage = ERRORS.UNAUTHORIZED_EMAIL
          } else {
            errorMessage = formatErrorMessage(errorBudget?.get('errorCode'))
          }

          setModal({
            isOpen: true,
            message: errorMessage,
            icon: DangerIcon,
            type: 'info',
            action: handleCloseModal,
          })
        }
        return setIsLoading(false)
      }

      if (isApproval && budget?.id) {
        const responsibleNameValue = getValues('responsibleName')
        const recipientInvoiceValue = getValues('recipientInvoice')
        const personPayingEmailValue = getValues('personPayingEmail')
        const responsibleDocNumberValue = getValues('responsibleDocNumber')
        const emailInvoiceValue = getValues('emailInvoice')
        const invoiceDocNumberValue = getValues('invoiceDocNumber')

        const payload = {
          responsibleName: responsibleNameValue,
          ...(recipientInvoiceValue === 'responsible_above'
            ? {
                personPayingEmail: personPayingEmailValue,
                personPayingCpf: responsibleDocNumberValue,
              }
            : {
                personPayingEmail: emailInvoiceValue,
                personPayingCpf: invoiceDocNumberValue,
              }),
        }

        return dispatch(approveBudget(serviceOrderId, budget?.id, payload))
          .then(() => {
            handleGetBudget()
            dispatch(getServiceOrder(serviceOrderId))
            if (isSAAS && serviceOrder?.isReferaService === false) {
              dispatch(
                getMessagingByServiceOrderId({
                  serviceOrderId,
                  destination: ROLE_GROUP_CHOICES.TRADESMAN_GROUP,
                  stepStatus: STEP_STATUS.WAITING_SERVICE_SCHEDULE,
                })
              )
                .then(response => {
                  if (serviceOrder?.tradesmanPhone && response?.whatsappText) {
                    openWhatsAppWithMessage({
                      phone: serviceOrder?.tradesmanPhone,
                      message: response.whatsappText,
                    })
                  }
                })
                .catch(() => {
                  console.error('Erro na requisição de procurar um registro de mensageria.')
                })
            }

            setModal({
              isOpen: true,
              message: 'Orçamento aprovado com sucesso.',
              icon: SuccessIcon,
              type: 'success',
              action: handleBackNavigation,
            })

            // FOR KANBAN
            if (onSubmitSuccess) onSubmitSuccess()

            return setIsLoading(false)
          })
          .catch(() => {
            setToast({
              isOpen: true,
              message: 'Ocorreu um erro ao realizar a operação.',
              type: 'error',
            })
            onFinalizesUpdateBudget({ updateError: true })

            return setIsLoading(false)
          })
      }

      // FOR KANBAN
      if (onSubmitSuccess) onSubmitSuccess()

      return setIsLoading(false)
    },
    [
      budget?.id,
      dispatch,
      isApproval,
      serviceOrderId,
      setToast,
      handleGetBudget,
      errorBudget,
      handleCloseModal,
      serviceOrder?.tradesmanPhone,
      isSAAS,
      serviceOrder?.isReferaService,
      setIsLoading,
    ]
  )

  const handleCheckErasedField = () => {
    if (currentBudgetIsApproved && payer) {
      return Object?.keys(data).some(field => data[field] === '' && payer[field])
    }
    return false
  }

  const handleFormHasErrors = useCallback(() => {
    let message = ''
    message = validateFormCpfCnpj(data)
    if (!!errors.whatsappResponsible || data?.whatsappResponsible.includes('_'))
      message = ERRORS.INVALID_TELEPHONE
    if (handleCheckErasedField()) {
      message = ERRORS.EMPTY_FIELD
    }
    if (personPayingEmailError || (data?.recipientInvoice === 'other' && emailInvoiceError)) {
      message = ERRORS.INVALID_EMAIL
    }
    if (message.length > 0) {
      setError({ isOpen: true, message })
      return true
    }
    setError({ isOpen: false, message: '' })
    return false
  }, [setError, data, errors, handleCheckErasedField, personPayingEmailError, emailInvoiceError])

  const buildBudgetRequestPayload = useCallback(
    dataValues => {
      const request = { ...dataValues }

      request.budget = Number(budgetId)
      request.keepPrivateData = false
      request.personPayingWhatsapp = dataValues.whatsappResponsible
      request.whatsappResponsible = undefined

      if (dataValues?.recipientInvoice === 'responsible_above') {
        request.nameInvoice = dataValues.responsibleName
        request.invoiceDocNumber = dataValues.responsibleDocNumber
        request.emailInvoice = dataValues.personPayingEmail
        request.taxRegime = dataValues.responsibleDocNumber.length === 18 ? dataValues.regime : ''
      }

      if (dataValues?.responsibleDocNumber?.length !== 18) {
        request.regime = ''
      }

      if (dataValues?.invoiceDocNumber?.length !== 18) {
        request.taxRegime = ''
      }

      if (dataValues?.installmentQuantity === '') request.installmentQuantity = 0

      return transformData(request)
    },
    [budgetId]
  )

  const persistPayerDataBeforePaymentLink = useCallback(
    async ({ showSuccessToastMessage = false } = {}) => {
      if (
        checkIfUserDoesNotPermission(PERMISSIONS.EDIT_PAYER_DATA, [
          PERMISSIONS_ACTIONS.ADD,
          PERMISSIONS_ACTIONS.EDIT,
        ])
      ) {
        return false
      }

      const dataValues = getValues()
      const isInstallmentValid = await handleInstallmentQuantity(dataValues?.installmentQuantity)
      if (!isInstallmentValid) {
        return false
      }

      if (isSubmitDisabled) {
        setToast({
          isOpen: true,
          message:
            'Não é possível salvar alterações pois o chamado se encontra em uma etapa que não permite edição dos dados do pagador.',
          type: 'error',
        })
        return false
      }

      if (!isApproval && !canManagePaymentLinkActions && !hasPermissionToEditPayerData) {
        setToast({
          isOpen: true,
          message:
            'Não é possível salvar alterações pois o chamado se encontra em uma etapa que não permite edição dos dados do pagador.',
          type: 'error',
        })
        return false
      }

      if (handleFormHasErrors()) return false

      try {
        await dispatch(
          updateBudget(serviceOrderId, budgetId, buildBudgetRequestPayload(dataValues))
        )
        if (showSuccessToastMessage) {
          setToast({
            isOpen: true,
            message: 'Dados do pagador salvos com sucesso.',
            type: 'success',
          })
        }
        return true
      } catch (e) {
        setToast({
          isOpen: true,
          message: e?.error_message ?? 'Ocorreu um erro ao salvar os dados do pagador.',
          type: 'error',
        })
        return false
      }
    },
    [
      budgetId,
      buildBudgetRequestPayload,
      canManagePaymentLinkActions,
      checkIfUserDoesNotPermission,
      dispatch,
      getValues,
      handleFormHasErrors,
      handleInstallmentQuantity,
      hasPermissionToEditPayerData,
      isApproval,
      isSubmitDisabled,
      serviceOrderId,
    ]
  )

  const refreshPaymentLinkContext = useCallback(async () => {
    await Promise.all([
      dispatch(getBudget(serviceOrderId, budgetId)),
      dispatch(getServiceOrder(serviceOrderId)),
      dispatch(getHistoryLogs(serviceOrderId)),
    ])
  }, [budgetId, dispatch, serviceOrderId])

  const persistManualPaymentLink = useCallback(async () => {
    if (!canManagePaymentLinkActions) {
      setToast({
        isOpen: true,
        message: 'Você não possui permissão para salvar o link manual de pagamento.',
        type: 'error',
      })
      return false
    }

    const normalizedManualPaymentLinkUrl = manualPaymentLinkUrl.trim()

    if (!normalizedManualPaymentLinkUrl) {
      setToast({
        isOpen: true,
        message: 'Informe a URL manual antes de salvar o formulário.',
        type: 'error',
      })
      return false
    }

    if (!isValidPaymentLinkUrl(normalizedManualPaymentLinkUrl)) {
      setToast({
        isOpen: true,
        message: 'Informe uma URL válida iniciando com https:// antes de salvar o formulário.',
        type: 'error',
      })
      return false
    }

    try {
      await dispatch(
        saveManualBudgetPaymentLink(serviceOrderId, budgetId, {
          url: normalizedManualPaymentLinkUrl,
          force: Boolean(paymentLinkData?.url),
        })
      )

      await refreshPaymentLinkContext()
      return true
    } catch (e) {
      setToast({
        isOpen: true,
        message: e?.error_message ?? 'Ocorreu um erro ao salvar o link manual de pagamento.',
        type: 'error',
      })
      return false
    }
  }, [
    budgetId,
    canManagePaymentLinkActions,
    dispatch,
    isValidPaymentLinkUrl,
    manualPaymentLinkUrl,
    paymentLinkData?.url,
    refreshPaymentLinkContext,
    serviceOrderId,
  ])

  const handleCopyPaymentLink = useCallback(async url => {
    if (!url) return
    try {
      await navigator.clipboard.writeText(url)
      setToast({
        isOpen: true,
        message: 'Link de pagamento copiado.',
        type: 'success',
      })
    } catch {
      setToast({
        isOpen: true,
        message: 'Não foi possível copiar o link de pagamento.',
        type: 'error',
      })
    }
  }, [])

  const handleGenerateOrSavePaymentLink = useCallback(async () => {
    if (automaticPaymentLinkBlockedReason) {
      setToast({
        isOpen: true,
        message: automaticPaymentLinkBlockedReason,
        type: 'error',
      })
      return
    }

    setIsLoading(true)

    const payerSaved = await persistPayerDataBeforePaymentLink()
    if (!payerSaved) {
      setIsLoading(false)
      return
    }

    try {
      await dispatch(
        generateBudgetPaymentLink(serviceOrderId, budgetId, {
          force: Boolean(paymentLinkData?.url),
        })
      )

      await refreshPaymentLinkContext()
      setToast({
        isOpen: true,
        message: 'Link de pagamento gerado com sucesso.',
        type: 'success',
      })
    } catch (e) {
      setToast({
        isOpen: true,
        message:
          e?.error_message ??
          'Ocorreu um erro inesperado, tente novamente ou gere o link manualmente pelo ASAAS',
        type: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }, [
    automaticPaymentLinkBlockedReason,
    budgetId,
    dispatch,
    paymentLinkData?.url,
    persistPayerDataBeforePaymentLink,
    refreshPaymentLinkContext,
    serviceOrderId,
    setIsLoading,
  ])

  const onSubmit = useCallback(
    async dataValues => {
      if (
        checkIfUserDoesNotPermission(PERMISSIONS.EDIT_PAYER_DATA, [
          PERMISSIONS_ACTIONS.ADD,
          PERMISSIONS_ACTIONS.EDIT,
        ])
      ) {
        return null
      }
      const isInstallmentValid = await handleInstallmentQuantity(dataValues?.installmentQuantity)

      if (!isInstallmentValid) {
        return false
      }

      setIsLoading(true)

      if (isSubmitDisabled) {
        setToast({
          isOpen: true,
          message:
            'Não é possível salvar alterações pois o chamado se encontra em uma etapa que não permite edição dos dados do pagador.',
          type: 'error',
        })
        return setIsLoading(false)
      }
      if (!isApproval && !canEditPayerData && !hasPermissionToEditPayerData) {
        setToast({
          isOpen: true,
          message:
            'Não é possível salvar alterações pois o chamado se encontra em uma etapa que não permite edição dos dados do pagador.',
          type: 'error',
        })

        return setIsLoading(false)
      }

      if (handleFormHasErrors()) return setIsLoading(false)

      try {
        await dispatch(
          updateBudget(serviceOrderId, budgetId, buildBudgetRequestPayload(dataValues))
        )

        if (isManualPaymentLink && canManagePaymentLinkActions) {
          const manualLinkSaved = await persistManualPaymentLink()
          if (!manualLinkSaved) {
            return setIsLoading(false)
          }
        }

        showToast({ type: 'success' })
        return onFinalizesUpdateBudget()
      } catch (e) {
        setToast({
          isOpen: true,
          message: e?.error_message ?? 'Ocorreu um erro ao realizar a operação.',
          type: 'error',
        })
        return onFinalizesUpdateBudget({ updateError: true })
      }
    },
    [
      budgetId,
      buildBudgetRequestPayload,
      dispatch,
      handleFormHasErrors,
      isSubmitDisabled,
      canManagePaymentLinkActions,
      isManualPaymentLink,
      handleInstallmentQuantity,
      onSubmitSuccess,
      onFinalizesUpdateBudget,
      persistManualPaymentLink,
      serviceOrderId,
      showToast,
    ]
  )

  const handleCloseToast = useCallback(() => {
    setToast(prevState => ({ ...prevState, isOpen: false }))
  }, [setToast])

  return (
    <>
      <Loader
        hasBackdrop
        open={loadingBudget || loadingApprove || isLoading || isAgencyLoading}
        label="Aguarde..."
      />
      <form id="payerForm" onSubmit={handleSubmit(onSubmit)}>
        <Grid>
          <Collapse in={error?.isOpen} className={styles.alert}>
            <Alert severity="info">
              <AlertTitle>{ERRORS.INVALID_FORM}</AlertTitle>
              {error?.message}
            </Alert>
          </Collapse>
          <Grid className={styles.subcontainer}>
            <InputLabel className={styles.label}>Dados do responsável pelo pagamento</InputLabel>
            <Grid className={styles.wrapper}>
              <SelectField
                id="payer"
                label="Responsável pelo pagamento"
                name="paymentResponsible"
                defaultValue={data?.paymentResponsible || ''}
                control={control}
                items={PAYER_OPTIONS}
                emptyItem
                required={isWaitingPaymentInfo}
              />
              <Grid className={styles.input}>
                <Typography className={styles.inputLabel}>
                  CPF/CNPJ do responsável
                  <Typography className={styles.asterisk}> *</Typography>
                </Typography>
                <TextFieldComponent
                  isCpfCnpj
                  name="responsibleDocNumber"
                  onChange={({ target: { value } }) =>
                    setValue('responsibleDocNumber', cpfCnpjMask(value))
                  }
                  value={watchCPF}
                  defaultValue={data?.responsibleDocNumber || ''}
                  required
                  placeholder="000.000.000-00"
                  className={styles.inputSize}
                  InputProps={{
                    style: { fontSize: 16, height: 40, marginTop: 4 },
                    inputProps: { maxLength: 18, minLength: 13 },
                  }}
                  InputLabelProps={{ shrink: true, className: styles.inputLabel }}
                />
              </Grid>
              <SelectField
                id="regime"
                label="Regime da empresa"
                name="regime"
                disabled={watchCPF.length !== 18}
                defaultValue={data?.regime || ''}
                control={control}
                items={REGIMEN_OPTIONS}
                emptyItem
                required={watchCPF.length === 18}
              />
            </Grid>
            <Grid className={styles.wrapper}>
              <InputField
                id="responsibleName"
                name="responsibleName"
                register={register}
                type="text"
                placeholder="Nome"
                label="Nome do responsável"
                defaultValue={data?.responsibleName || ''}
                required
              />
              <InputField
                id="personPayingEmail"
                name="personPayingEmail"
                type="email"
                placeholder="Digite o e-mail"
                label="E-mail do responsável"
                register={register}
                defaultValue={data?.personPayingEmail || ''}
                required
                disabled={!data?.personPayingEmail}
                onChange={handleChange}
              />
              <Grid className={styles.input}>
                <Typography className={styles.inputLabel}>
                  Whatsapp do responsável
                  <Typography className={styles.asterisk}> *</Typography>
                </Typography>
                <Controller
                  control={control}
                  name="whatsappResponsible"
                  rules={{ required: true, minLength: 9 }}
                  defaultValue={data?.whatsappResponsible || ''}
                  as={
                    <InputMask
                      mask="(99) 9 9999-9999"
                      maskChar="_"
                      required
                      InputLabelProps={{ shrink: true, className: styles.inputLabel }}
                      InputProps={{
                        style: { fontSize: 16, height: 35, marginTop: 10 },
                        inputProps: { minLength: 9 },
                      }}
                    >
                      {inputProps => <TextField className={styles.inputSize} {...inputProps} />}
                    </InputMask>
                  }
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid className={styles.subcontainer}>
            <InputLabel className={styles.label}>Dados do orçamento</InputLabel>
            <Grid className={styles.wrapper}>
              <SelectField
                id="payCollector"
                label="Responsável pela cobrança"
                name="payCollector"
                defaultValue={data?.payCollector || ''}
                control={control}
                items={CHARGING_OPTIONS}
                disabled={isFromSAASAgencyAndNotReferaService || hasPermissionToEditPayerData}
                emptyItem
                required={
                  (currentBudgetIsApproved || isApproval || isWaitingPaymentInfo) &&
                  !data?.payCollector
                }
              />
              <SelectField
                id="formOfPayment"
                label="Forma de pagamento"
                name="formOfPayment"
                defaultValue={data?.formOfPayment || ''}
                control={control}
                items={PAYMENT_OPTIONS}
                disabled={hasPermissionToEditPayerData}
                emptyItem
                required={
                  (currentBudgetIsApproved || isApproval || isWaitingPaymentInfo) &&
                  !data?.formOfPayment &&
                  data?.payCollector
                }
              />
              <SelectField
                emptyItem
                id="installmentQuantity"
                name="installmentQuantity"
                label="Parcelamento desejado"
                defaultValue={data?.installmentQuantity || ''}
                control={control}
                items={installmentSelectOptions}
                disabled={hasPermissionToEditPayerData}
                required={
                  (currentBudgetIsApproved || isApproval || isWaitingPaymentInfo) &&
                  !data?.installmentQuantity
                }
              />
            </Grid>
          </Grid>
          <Grid className={styles.subcontainer}>
            <InputLabel className={styles.label}>Dados da NFS-e</InputLabel>
            <Grid className={styles.wrapper}>
              <SelectField
                id="recipientInvoice"
                label="Emitir NFS-e para"
                name="recipientInvoice"
                defaultValue={data?.recipientInvoice || ''}
                control={control}
                items={RECIPIENT_OPTIONS}
                emptyItem
                required={
                  (currentBudgetIsApproved || isApproval || isWaitingPaymentInfo) &&
                  !data?.recipientInvoice
                }
              />
              {data?.recipientInvoice === 'other' && (
                <>
                  <Grid className={styles.input}>
                    <Typography className={styles.inputLabel}>
                      CPF/CNPJ do responsável
                      <Typography className={styles.asterisk}> *</Typography>
                    </Typography>
                    <TextFieldComponent
                      isCpfCnpj
                      name="invoiceDocNumber"
                      onChange={e => setValue('invoiceDocNumber', cpfCnpjMask(e.target.value))}
                      value={watchInvoiceDocNumber}
                      defaultValue={data?.invoiceDocNumber || ''}
                      required={data?.recipientInvoice === 'other'}
                      placeholder="000.000.000-00"
                      className={styles.inputSize}
                      InputProps={{
                        style: { fontSize: 16, height: 40, marginTop: 4 },
                        inputProps: { maxLength: 18, minLength: 13 },
                      }}
                      InputLabelProps={{ shrink: true, className: styles.inputLabel }}
                    />
                  </Grid>
                  <InputField
                    id="nameInvoice"
                    name="nameInvoice"
                    onChange={e => setValue('nameInvoice', e.target.value)}
                    defaultValue={watchInvoiceName || ''}
                    control={control}
                    type="text"
                    placeholder="Nome"
                    label="Nome para NFS-e"
                    required={data?.recipientInvoice === 'other'}
                  />
                </>
              )}
            </Grid>
            {data?.recipientInvoice === 'other' && (
              <Grid className={styles.wrapper}>
                <InputField
                  id="emailInvoice"
                  name="emailInvoice"
                  type="email"
                  defaultValue={watchInvoiceEmail || ''}
                  control={control}
                  placeholder="Digite o e-mail"
                  label="E-mail para NFS-e"
                  required={data?.recipientInvoice === 'other'}
                  onChange={handleChange}
                />
                {data?.invoiceDocNumber?.length === 18 && (
                  <SelectField
                    id="taxRegime"
                    label="Regime tributário"
                    name="taxRegime"
                    defaultValue={watchTaxRegime || ''}
                    control={control}
                    items={REGIMEN_OPTIONS}
                    emptyItem
                    required={data?.invoiceDocNumber?.length === 18}
                  />
                )}
              </Grid>
            )}
          </Grid>
          {shouldRenderPaymentLinkSection && (
            <Grid className={styles.subcontainer}>
              <InputLabel className={styles.label}>Link de pagamento</InputLabel>
              <Grid className={styles.paymentLinkContainer}>
                {paymentLinkData?.status === 'failed' && paymentLinkData?.failureReason && (
                  <Alert severity="error" className={styles.helperText}>
                    {paymentLinkData.failureReason}
                  </Alert>
                )}
                {paymentLinkBlockedReason && (
                  <Alert severity="info" className={styles.helperText}>
                    {paymentLinkBlockedReason}
                  </Alert>
                )}

                {paymentLinksData.length > 0 && (
                  <>
                    <Typography variant="body2" className={styles.paymentLinkInfoTitle}>
                      Links de pagamento
                    </Typography>
                    <Grid component="ul" className={styles.paymentLinkInfo}>
                      {paymentLinksData.map(paymentLink => (
                        <Grid
                          component="li"
                          className={styles.paymentLinkItem}
                          key={paymentLink.id}
                        >
                          <Grid className={styles.paymentLinkItemContent}>
                            {isValidPaymentLinkUrl(paymentLink?.url) ? (
                              <Typography
                                component="a"
                                href={paymentLink?.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="body2"
                                className={styles.paymentLinkUrl}
                              >
                                {paymentLink?.url}
                              </Typography>
                            ) : (
                              <Typography variant="body2" className={styles.paymentLinkUrl}>
                                {paymentLink?.url}
                              </Typography>
                            )}
                          </Grid>
                          <IconButton
                            size="small"
                            onClick={() => handleCopyPaymentLink(paymentLink?.url)}
                            className={styles.paymentLinkCopyButton}
                            aria-label="Copiar link"
                          >
                            <DocumentCopy color={Theme.Colors.Primary.Base} fontSize={20} />
                          </IconButton>
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}

                {canInteractWithPaymentLinkActions && (
                  <FormControlLabel
                    className={styles.manualToggle}
                    control={
                      <Checkbox
                        color="primary"
                        checked={isManualPaymentLink}
                        onChange={event => setIsManualPaymentLink(event.target.checked)}
                      />
                    }
                    label="Informar link manualmente"
                  />
                )}

                {canInteractWithPaymentLinkActions && isManualPaymentLink && (
                  <Grid className={styles.manualFields}>
                    <InputField
                      id="manualPaymentLinkUrl"
                      name="manualPaymentLinkUrl"
                      type="text"
                      label="URL manual"
                      placeholder="https://..."
                      value={manualPaymentLinkUrl}
                      onChange={event => setManualPaymentLinkUrl(event.target.value)}
                      className={styles.manualField}
                    />
                  </Grid>
                )}

                <Grid className={styles.paymentLinkActions}>
                  {canInteractWithPaymentLinkActions && (
                    <Button
                      type="button"
                      color="primary"
                      variant="contained"
                      onClick={handleGenerateOrSavePaymentLink}
                      disabled={
                        isLoading ||
                        Boolean(automaticPaymentLinkBlockedReason) ||
                        isManualPaymentLink
                      }
                    >
                      Gerar link
                    </Button>
                  )}
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </form>
      {alertMessage && <ConfirmationDialog type="warning" subject={alertMessage} />}
      <Dialog
        open={modal?.isOpen}
        icon={modal?.icon}
        type={modal?.type}
        subject={modal?.message}
        onApprove={modal?.action}
      />
      {toast.isOpen && (
        <Toast draggable open={toast.isOpen} autoHideDuration={6000} onClose={handleCloseToast}>
          <Alert severity={toast.type} title={toast.message} onClose={handleCloseToast} />
        </Toast>
      )}
    </>
  )
}
